import { useState, useCallback, useEffect } from 'react';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { authApi, passkeyApi, ApiRequestError } from '@api/client';
import type { AdminUser } from '@api/types';
import { useRecentAccounts } from '@hooks/useRecentAccounts';

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

export interface AuthState {
  token: string | null;
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface UseAuthReturn extends AuthState {
  /** Request a passwordless magic-link email (invite-only on the server). */
  requestMagicLink: (email: string) => Promise<void>;
  /** Exchange a magic-link token for a session and persist it. */
  completeMagicLink: (token: string) => Promise<void>;
  /** Discoverable-credential WebAuthn sign-in. Browser shows passkey picker. */
  signInWithPasskey: () => Promise<void>;
  /**
   * Continue button: intenta passkey discovery; si falla (NotAllowedError,
   * sin resident-key disponible, etc.) hace fallback a magic-link al email.
   * Devuelve true si la passkey funcionó (autenticado), false si cayó al magic-link.
   */
  trySignInOrMagic: (email: string) => Promise<boolean>;
  /** Register the current device as a passkey for the active session. */
  registerPasskey: (name?: string) => Promise<void>;
  /** Revoke server-side session and clear local state (sin tocar cuentas recientes). */
  logout: () => Promise<void>;
  hasRole: (role: string) => boolean;
}

/**
 * Auth hook — manages the session token in localStorage. Login is passwordless
 * (BetterAuth magic link). The token is injected into API calls by the client.
 */
export function useAuth(): UseAuthReturn {
  const recentAccounts = useRecentAccounts();
  const [state, setState] = useState<AuthState>(() => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const userRaw = localStorage.getItem(USER_KEY);
      const user = userRaw ? (JSON.parse(userRaw) as AdminUser) : null;
      return { token, user, isAuthenticated: !!token, isLoading: false, error: null };
    } catch {
      return { token: null, user: null, isAuthenticated: false, isLoading: false, error: null };
    }
  });

  // Sync across tabs
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === TOKEN_KEY || e.key === USER_KEY) {
        const token = localStorage.getItem(TOKEN_KEY);
        const userRaw = localStorage.getItem(USER_KEY);
        const user = userRaw ? (JSON.parse(userRaw) as AdminUser) : null;
        setState({ token, user, isAuthenticated: !!token, isLoading: false, error: null });
      }
    }
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener('storage', onStorage); };
  }, []);

  const requestMagicLink = useCallback(async (email: string) => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      await authApi.requestMagicLink(email);
      setState((s) => ({ ...s, isLoading: false, error: null }));
    } catch (err) {
      const message =
        err instanceof ApiRequestError ? err.message : 'auth.loginError';
      setState((s) => ({ ...s, isLoading: false, error: message }));
      throw err;
    }
  }, []);

  const completeMagicLink = useCallback(async (token: string) => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const { token: sessionToken, user } = await authApi.verifyMagicLink(token);
      localStorage.setItem(TOKEN_KEY, sessionToken);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      setState({
        token: sessionToken,
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      recentAccounts.upsert(user);
    } catch (err) {
      const message =
        err instanceof ApiRequestError ? err.message : 'auth.loginError';
      setState((s) => ({ ...s, isLoading: false, error: message }));
      throw err;
    }
  }, [recentAccounts]);

  const signInWithPasskey = useCallback(async () => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const options = await passkeyApi.authenticateOptions();
      const assertion = await startAuthentication({ optionsJSON: options as never });
      const { token: sessionToken, user } = await passkeyApi.authenticateVerify({ response: assertion });
      localStorage.setItem(TOKEN_KEY, sessionToken);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      setState({ token: sessionToken, user, isAuthenticated: true, isLoading: false, error: null });
      recentAccounts.upsert(user);
    } catch (err) {
      console.error('[passkey] signIn failed:', err);
      const message =
        err instanceof ApiRequestError ? err.message : 'auth.passkeyError';
      setState((s) => ({ ...s, isLoading: false, error: message }));
      throw err;
    }
  }, [recentAccounts]);

  const trySignInOrMagic = useCallback(async (email: string): Promise<boolean> => {
    // Sin email no podemos hacer fallback a magic link.
    const trimmed = email.trim();
    if (!trimmed) throw new Error('Email requerido');

    // Intento 1: passkey discovery. Si el usuario tiene una passkey resident-key
    // en este dispositivo el navegador la sugiere; si elige una, autentica.
    try {
      await signInWithPasskey();
      return true;
    } catch {
      // Fallback silencioso: enviamos magic link al email tecleado.
      await requestMagicLink(trimmed);
      return false;
    }
  }, [signInWithPasskey, requestMagicLink]);

  const registerPasskey = useCallback(async (name?: string) => {
    try {
      const options = await passkeyApi.registerOptions(name ? { name } : undefined);
      const attestation = await startRegistration({ optionsJSON: options as never });
      await passkeyApi.registerVerify({ response: attestation, ...(name ? { name } : {}) });
    } catch (err) {
      console.error('[passkey] register failed:', err);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    // Best-effort server revoke before clearing local state.
    await authApi.signOut();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    // NO se limpia `admin_recent_accounts`: el saludo y la card "Continuar como X"
    // siguen activos en la próxima visita. El usuario puede quitar la cuenta
    // explícitamente con el botón × del listado.
    setState({ token: null, user: null, isAuthenticated: false, isLoading: false, error: null });
  }, []);

  const hasRole = useCallback(
    (role: string) => state.user?.role === role,
    [state.user],
  );

  return {
    ...state,
    requestMagicLink,
    completeMagicLink,
    signInWithPasskey,
    trySignInOrMagic,
    registerPasskey,
    logout,
    hasRole,
  };
}
